/** VUEX module for state management in app **/
import { TeamsApi } from '@/services/api-teams.js';

/**
 * Filter out teams that have matching ids
 * @param {Array} AllTeamsOfType - all teams collection of a certain type (dev or org)
 * @param {Array} userTeamsOfType - teams a user belongs to of the same type.
 * @returns {Array} - all teams that aren't the user's
 */
function filterTeams(AllTeamsOfType, userTeamsOfType) {
  return AllTeamsOfType.filter((team) => {
    return userTeamsOfType.findIndex((userTeam) => userTeam.id === team.id) === -1;
  });
}

// Local state -----------------------------------------------------
export const state = {
  teams: {
    user: {
      orgs: [],
      developers: [],
    },
    all: {
      orgs: [],
      developers: [],
    },
  },
};

// Computed properties of the state --------------------------------
export const getters = {
  // current user's teams
  userTeams: (state) => state.teams.user,
  userOrgTeams: (state, getters) => getters.userTeams.orgs || [],
  userDevTeams: (state, getters) => getters.userTeams.developers || [],

  // all teams (super-admins)
  allTeams: (state) => state.teams.all,

  // all teams except for the teams a user belongs to
  allOrgTeams: (state, getters) => {
    let teams = getters.allTeams.orgs || [];
    return filterTeams(teams, getters.userOrgTeams);
  },

  allDevTeams: (state, getters) => {
    let teams = getters.allTeams.developers || [];
    return filterTeams(teams, getters.userDevTeams);
  },

  teamCount: (state, getters) => {
    return getters.userOrgTeams.length + getters.userDevTeams.length;
  },

  allTeamCount: (state, getters) => {
    return getters.allOrgTeams.length + getters.allDevTeams.length;
  },

  teamById: (state) => (teamId) => {
    // gather all teams into one array
    let teams = [
      ...state.teams.user.orgs,
      ...state.teams.user.developers,
      ...state.teams.all.orgs,
      ...state.teams.all.developers,
    ];

    return teams.find((team) => team.id === teamId);
  },
};

// Synchronous mutation of the local state -------------------------
export const mutations = {
  SET_USER_TEAMS(state, teams) {
    state.teams.user = teams;
  },

  SET_ALL_TEAMS(state, teams) {
    state.teams.all = teams;
  },

  SET_USER_TEAM(state, { team, type }) {
    state.teams.user[type].unshift(team);
  },

  SET_CACHED(state, { group, cached }) {
    state.cached[group] = cached;
  },
};

// Async CRUD actions against the api ------------------------------
export const actions = {
  /**
   * Get all the teams a user has access to
   */
  all({ commit, state }) {
    return TeamsApi.all()
      .then((teams) => {
        commit('SET_ALL_TEAMS', teams);
        return state.teams.all;
      })
      .catch(() => {
        commit('SET_ALL_TEAMS', {});
      });
  },

  /**
   * Get all the teams a user belongs to
   */
  index({ commit, state }) {
    return TeamsApi.index()
      .then((teams) => {
        commit('SET_USER_TEAMS', teams);
        return state.teams.user;
      })
      .catch(() => {
        commit('SET_USER_TEAMS', {});
      });
  },

  // CREATE a single item
  store({ commit }, { data }) {
    return TeamsApi.store(data).then((team) => {
      commit('SET_USER_TEAM', { team, type: 'developers' });
      return team;
    });
  },
};
